/**
 * About page sub-nav scroll spy.
 * Highlights the nav link for the section nearest the sticky header line;
 * CSS fades siblings when one link has .is-active.
 */

let cleanup = null;

function getScrollOffset() {
  const nav = document.querySelector(".c-content-page__nav");
  if (!nav) {
    return 0;
  }

  const top = parseFloat(getComputedStyle(nav).top);
  return Number.isFinite(top) ? top : 0;
}

/**
 * Resolve a box-generating element for anchors that use display: contents
 * (e.g. #over-ons / .c-text-blocks).
 */
function getObservableTarget(el) {
  if (!el) {
    return null;
  }

  let node = el;
  while (node && getComputedStyle(node).display === "contents") {
    node = node.firstElementChild;
  }

  return node || el;
}

function getDocumentTop(el) {
  const target = getObservableTarget(el);
  if (!target) {
    return Infinity;
  }

  return target.getBoundingClientRect().top + window.scrollY;
}

function setActiveLink(links, activeLink) {
  links.forEach((link) => {
    const isActive = link === activeLink;
    link.classList.toggle("is-active", isActive);

    if (isActive) {
      link.setAttribute("aria-current", "true");
    } else {
      link.removeAttribute("aria-current");
    }
  });
}

export function destroyAboutNav() {
  if (cleanup) {
    cleanup();
    cleanup = null;
  }
}

export function initAboutNav() {
  destroyAboutNav();

  const nav = document.querySelector(".c-content-page__nav");
  if (!nav) {
    return;
  }

  const links = Array.from(nav.querySelectorAll(".c-content-page__nav-link[href^='#']"));
  if (links.length === 0) {
    return;
  }

  const items = links
    .map((link) => {
      const id = decodeURIComponent(link.hash.slice(1));
      const section = id ? document.getElementById(id) : null;
      return section ? { link, section } : null;
    })
    .filter(Boolean);

  if (items.length === 0) {
    return;
  }

  let ticking = false;

  const update = () => {
    ticking = false;
    const marker = window.scrollY + getScrollOffset() + 1;
    const ordered = items
      .map((item) => ({ ...item, top: getDocumentTop(item.section) }))
      .sort((a, b) => a.top - b.top);

    let active = ordered[0];

    for (const item of ordered) {
      if (item.top <= marker) {
        active = item;
      }
    }

    // Near page bottom: keep the last section in document order active
    const docBottom = window.scrollY + window.innerHeight;
    const pageBottom = document.documentElement.scrollHeight;
    if (pageBottom - docBottom < 4) {
      active = ordered[ordered.length - 1];
    }

    setActiveLink(
      items.map((item) => item.link),
      active.link
    );
  };

  const onScrollOrResize = () => {
    if (ticking) {
      return;
    }
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);

  update();

  cleanup = () => {
    window.removeEventListener("scroll", onScrollOrResize);
    window.removeEventListener("resize", onScrollOrResize);
    setActiveLink(
      items.map((item) => item.link),
      null
    );
  };
}
